import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useStore } from '../context/StoreContext';
import { api } from '../services/api';
import { BillingConfig, BillingPlanKey, Company, StaffPermission, StaffAccessProfile, SubscriptionStatus, UserRole } from '../types';
import { AlertTriangle, CalendarDays, CheckCircle, ChevronRight, CreditCard, Headphones, Lock, RefreshCw, ShieldCheck } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
  const { user, currentCompany } = useStore();
  const location = useLocation();
  const isLocalhost = typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname);

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 p-4">
        <div className="max-w-sm rounded-2xl border border-gray-100 bg-white p-8 text-center shadow-sm">
          <Lock className="mx-auto mb-4 h-12 w-12 text-gray-300" />
          <h2 className="mb-2 text-xl font-bold text-gray-800">Acesso negado</h2>
          <p className="text-sm text-gray-500">Seu perfil ({user.role}) não tem permissão para acessar esta área.</p>
        </div>
      </div>
    );
  }

  if (user.role === UserRole.OWNER && currentCompany?.useSubLogin && location.pathname !== '/access' && sessionStorage.getItem('ft_subaccess_active') !== 'v2') {
    return <Navigate to="/access" replace />;
  }

  const routePermissions: Array<{ prefix: string; permission: StaffPermission }> = [
    { prefix: '/pos', permission: 'POS' },
    { prefix: '/menu', permission: 'PRODUCTS' },
    { prefix: '/stock', permission: 'STOCK' },
    { prefix: '/reports', permission: 'REPORTS' },
    { prefix: '/settings', permission: 'SETTINGS' },
    { prefix: '/kitchen', permission: 'KITCHEN' },
    { prefix: '/delivery', permission: 'DELIVERY' },
  ];
  const routePermission = routePermissions.find(item => location.pathname === item.prefix || location.pathname.startsWith(`${item.prefix}/`))?.permission;
  const defaultPermissions: Record<StaffAccessProfile, StaffPermission[]> = {
    EMPLOYEE: ['POS', 'PRODUCTS', 'KITCHEN', 'DELIVERY'],
    MANAGER: ['POS', 'PRODUCTS', 'STOCK', 'REPORTS', 'SETTINGS', 'KITCHEN', 'DELIVERY'],
    COUNTER: ['POS']
  };
  const staffPermissions = user.accessPermissions || (user.accessProfile ? defaultPermissions[user.accessProfile] : null);

  if (user.role === UserRole.CASHIER && routePermission && staffPermissions && !staffPermissions.includes(routePermission)) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 p-4">
        <div className="max-w-sm rounded-2xl border border-gray-100 bg-white p-8 text-center shadow-sm">
          <Lock className="mx-auto mb-4 h-12 w-12 text-gray-300" />
          <h2 className="mb-2 text-xl font-bold text-gray-800">Área não autorizada</h2>
          <p className="text-sm text-gray-500">O dono da empresa ainda não liberou esta área para o seu perfil.</p>
        </div>
      </div>
    );
  }

  if (user.role === UserRole.SAAS_ADMIN) {
    return <>{children}</>;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const billingDate = currentCompany?.nextBillingDate ? new Date(currentCompany.nextBillingDate) : null;
  if (billingDate) billingDate.setHours(23, 59, 59, 999);
  const isExpired = Boolean(billingDate && today > billingDate);
  const isSuspended = currentCompany?.subscriptionStatus === SubscriptionStatus.CANCELED
    || currentCompany?.subscriptionStatus === SubscriptionStatus.PENDING;
  const mercadoPagoStatus = String(currentCompany?.mercadoPagoStatus || '').toLowerCase();
  const isUnpaidMercadoPagoSubscription = currentCompany?.billingProvider === 'MERCADOPAGO'
    && ['pending', 'paused', 'cancelled', 'canceled', 'cancelled_process', 'inactive', 'rejected', 'expired'].includes(mercadoPagoStatus)
    && !['authorized', 'active'].includes(mercadoPagoStatus);

  if (!isLocalhost && (isSuspended || isExpired || isUnpaidMercadoPagoSubscription)) {
    const blockMessage = isExpired
      ? `Seu plano ${currentCompany?.subscriptionStatus === SubscriptionStatus.TRIAL ? 'de teste' : 'mensal'} expirou em ${billingDate?.toLocaleDateString('pt-BR')}. Renove sua assinatura para continuar operando.`
      : 'O acesso deste estabelecimento está pausado. Fale com a equipe FlowTab para regularizar.';

    return (
      <SubscriptionBlockScreen
        title={isExpired ? 'Vencimento expirado' : 'Acesso suspenso'}
        message={blockMessage}
        company={currentCompany}
      />
    );
  }

  return <>{children}</>;
};

const SubscriptionBlockScreen = ({ title, message, company }: { title: string; message: string; company?: Company | null }) => {
  const [billingConfig, setBillingConfig] = useState<BillingConfig | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<BillingPlanKey>(company?.plan || 'PRO');
  const [loadingPlan, setLoadingPlan] = useState<BillingPlanKey | null>(null);
  const [error, setError] = useState('');
  const [showOtherPlans, setShowOtherPlans] = useState(false);
  const whatsappUrl = 'https://wa.me/5561998866211?text=Ol%C3%A1!%20Gostaria%20de%20regularizar%20minha%20assinatura%20do%20sistema%20FlowTab.';

  useEffect(() => {
    let mounted = true;

    api.billing.getConfig()
      .then(config => {
        if (!mounted || !config) return;
        setBillingConfig(config);
        setSelectedPlan(currentPlan => (
          !config.plans.some(plan => plan.plan === currentPlan) && config.plans[0]
            ? config.plans[0].plan
            : currentPlan
        ));
      })
      .catch(() => {
        if (mounted) setError('Não foi possível carregar os planos agora.');
      });

    return () => {
      mounted = false;
    };
  }, []);

  const handleRegularize = async (plan: BillingPlanKey) => {
    if (!company?.id) {
      setError('Empresa não encontrada para gerar a cobrança.');
      return;
    }

    setError('');
    setLoadingPlan(plan);
    try {
      const response = await api.billing.createMercadoPagoSubscription(company.id, plan);
      if (response?.error) {
        setError(response.error);
        return;
      }
      if (response?.initPoint) {
        window.location.href = response.initPoint;
        return;
      }
      setError('Não foi possível gerar o link de pagamento agora. Chame o suporte FlowTab.');
    } catch (err) {
      setError('Não foi possível iniciar o pagamento. Tente novamente ou chame o suporte.');
    } finally {
      setLoadingPlan(null);
    }
  };

  const plans = billingConfig?.plans || [];
  const selectedPlanConfig = plans.find(plan => plan.plan === selectedPlan);
  const customMonthlyPrice = Number(company?.monthlyPriceOverride);
  const hasCustomMonthlyPrice = Number.isFinite(customMonthlyPrice) && customMonthlyPrice >= 1;
  const companyPlanConfig = plans.find(plan => plan.plan === company?.plan);
  const visiblePlans = hasCustomMonthlyPrice && !showOtherPlans && companyPlanConfig
    ? [companyPlanConfig]
    : plans;
  const selectedPlanPrice = selectedPlanConfig
    ? hasCustomMonthlyPrice && selectedPlanConfig.plan === company?.plan
      ? customMonthlyPrice
      : selectedPlanConfig.price
    : 0;
  const formattedDueDate = company?.nextBillingDate
    ? new Date(company.nextBillingDate).toLocaleDateString('pt-BR')
    : 'sem data definida';

  useEffect(() => {
    if (company?.plan) setSelectedPlan(company.plan);
  }, [company?.plan]);

  return (
    <div className="min-h-screen bg-slate-950 p-3 text-slate-950 sm:p-5">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.26),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(37,99,235,0.2),transparent_38%)]" />
      <div className="relative mx-auto flex min-h-screen max-w-7xl items-center justify-center py-6">
        <div className="grid w-full overflow-hidden rounded-[2rem] border border-white/15 bg-white shadow-2xl shadow-emerald-950/30 lg:grid-cols-[0.88fr_1.12fr]">
          <section className="relative overflow-hidden bg-slate-950 p-6 text-white sm:p-8 lg:p-10">
            <div className="absolute -left-24 top-20 h-72 w-72 rounded-full bg-emerald-400/10 blur-3xl" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-300/20 bg-emerald-300/10 px-4 py-2 text-[11px] font-black uppercase tracking-[0.22em] text-emerald-200">
                <ShieldCheck className="h-4 w-4" />
                Painel seguro FlowTab
              </div>

              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                <div className="rounded-[1.4rem] border border-white/10 bg-white/5 p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-red-500/10 text-red-200 ring-1 ring-red-300/20">
                      <Lock className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Status</p>
                      <p className="text-sm font-black text-white">{title}</p>
                    </div>
                  </div>
                </div>
                <div className="rounded-[1.4rem] border border-white/10 bg-white/5 p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-400/10 text-emerald-200 ring-1 ring-emerald-300/20">
                      <CalendarDays className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Vencimento</p>
                      <p className="text-sm font-black text-white">{formattedDueDate}</p>
                    </div>
                  </div>
                </div>
              </div>

              <h1 className="mt-7 text-3xl font-black tracking-tight sm:text-4xl lg:text-5xl">{title}</h1>
              <p className="mt-4 max-w-xl text-sm font-semibold leading-7 text-slate-300">{message}</p>

              <div className="mt-8 space-y-3">
                {[
                  'Escolha um plano ativo para o estabelecimento.',
                  'Confirme a assinatura no ambiente seguro do Mercado Pago.',
                  'A liberação volta automaticamente após a aprovação.'
                ].map((step, index) => (
                  <div key={step} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] p-3">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-400/10 text-xs font-black text-emerald-200 ring-1 ring-emerald-300/20">
                      {index + 1}
                    </span>
                    <p className="text-sm font-bold text-slate-300">{step}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="bg-gradient-to-b from-white to-slate-50 p-5 sm:p-6 lg:p-8">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.22em] text-emerald-600">Regularizar assinatura</p>
                <h2 className="mt-2 text-2xl font-black text-slate-950 sm:text-3xl">
                  {hasCustomMonthlyPrice ? 'Seu plano exclusivo' : 'Escolha o melhor plano'}
                </h2>
                <p className="mt-1 text-sm font-semibold text-slate-500">
                  {hasCustomMonthlyPrice ? 'Condição especial já aplicada para renovar seu acesso.' : 'Planos mensais, liberação automática e suporte direto da equipe FlowTab.'}
                </p>
              </div>
              <span className="w-fit rounded-full bg-slate-100 px-3 py-1.5 text-xs font-black text-slate-500">{company?.name || 'FlowTab'}</span>
            </div>

            {error && (
              <div className="mt-5 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
                {error}
              </div>
            )}

            <div className="mt-5 grid gap-3">
              {plans.length === 0 ? (
                <div className="rounded-3xl border border-slate-100 bg-slate-50 p-6 text-center text-sm font-bold text-slate-500">
                  Carregando planos...
                </div>
              ) : visiblePlans.map(plan => {
                const active = selectedPlan === plan.plan;
                const enabledFeatures = Object.entries(plan.features).filter(([, enabled]) => enabled).length;
                const customPriceForThisPlan = hasCustomMonthlyPrice && plan.plan === company?.plan;
                const displayPrice = customPriceForThisPlan ? customMonthlyPrice : plan.price;
                return (
                  <button
                    key={plan.plan}
                    type="button"
                    onClick={() => setSelectedPlan(plan.plan)}
                    className={`rounded-[1.6rem] border p-4 text-left transition-all ${active ? 'border-emerald-300 bg-emerald-50 shadow-lg shadow-emerald-100' : 'border-slate-100 bg-white hover:border-slate-200 hover:bg-slate-50'}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-xl font-black text-slate-950">{plan.name}</h3>
                          {customPriceForThisPlan && <span className="rounded-full bg-slate-950 px-2 py-1 text-[10px] font-black uppercase text-white">Exclusivo</span>}
                          {plan.plan === 'PRO' && <span className="rounded-full bg-emerald-600 px-2 py-1 text-[10px] font-black uppercase text-white">Indicado</span>}
                        </div>
                        <p className="mt-1 text-xs font-bold text-slate-500">
                          {customPriceForThisPlan ? 'Preço exclusivo FlowTab' : `${enabledFeatures} recursos ativos neste pacote`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-black text-slate-950">{displayPrice.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">por mês</p>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {Object.entries(plan.features).filter(([, enabled]) => enabled).slice(0, 5).map(([key]) => (
                        <span key={key} className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-[10px] font-black text-slate-600 ring-1 ring-slate-100">
                          <CheckCircle className="h-3 w-3 text-emerald-500" />
                          {billingConfig?.featureCatalog[key] || key}
                        </span>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>

            {hasCustomMonthlyPrice && plans.length > 1 && (
              <button
                type="button"
                onClick={() => setShowOtherPlans(current => !current)}
                className="mt-3 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700 transition hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700"
              >
                {showOtherPlans ? 'Mostrar só meu plano exclusivo' : 'Ver outros planos'}
              </button>
            )}

            {selectedPlanConfig && (
              <div className="mt-4 rounded-[1.4rem] border border-slate-200 bg-white p-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Plano selecionado</p>
                    <p className="text-lg font-black text-slate-950">
                      {hasCustomMonthlyPrice && selectedPlanConfig.plan === company?.plan ? `${selectedPlanConfig.name} exclusivo` : selectedPlanConfig.name}
                    </p>
                  </div>
                  <p className="text-2xl font-black text-slate-950">{selectedPlanPrice.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}<span className="ml-1 text-[10px] uppercase tracking-widest text-slate-400">/mês</span></p>
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={() => handleRegularize(selectedPlan)}
              disabled={!billingConfig?.checkoutConfigured || Boolean(loadingPlan)}
              className="mt-4 flex w-full items-center justify-between rounded-[1.4rem] bg-slate-950 px-6 py-5 font-black text-white shadow-xl shadow-slate-200 transition-all hover:-translate-y-0.5 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="flex items-center gap-3">
                {loadingPlan ? <RefreshCw className="h-5 w-5 animate-spin" /> : <CreditCard className="h-5 w-5" />}
                Assinar com Mercado Pago
              </span>
              <ChevronRight className="h-5 w-5" />
            </button>

            {!billingConfig?.checkoutConfigured && (
              <p className="mt-3 flex items-start gap-2 rounded-2xl bg-amber-50 px-4 py-3 text-xs font-bold text-amber-700">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                Pagamento online ainda não configurado. Use o suporte para regularizar agora.
              </p>
            )}

            <div className="mt-5 flex flex-col gap-3 border-t border-slate-100 pt-5 sm:flex-row">
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-emerald-50 px-4 py-3 text-sm font-black text-emerald-700 transition hover:bg-emerald-100">
                <Headphones className="h-4 w-4" />
                Chamar suporte
              </a>
              <div className="flex-1 rounded-2xl bg-slate-50 px-4 py-3 text-center text-xs font-bold text-slate-500">
                central@flowtab.com.br<br />+55 61 99886-6211
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};
